import { useCallback, useRef, useState } from "react";

/**
 * Tracks an element's rendered width via `ResizeObserver`.
 *
 * Returns a callback ref rather than a `useRef`/`useEffect` pair because the
 * consuming element is often conditionally rendered: an effect with empty
 * deps would latch onto a null ref on first render and never observe the
 * element once it mounts. Width is `0` until the first measurement.
 *
 * Useful for sizing chart internals (tick fonts, stroke widths, etc.) off of
 * the actual rendered container width rather than a viewport breakpoint —
 * the two can diverge whenever the container's width depends on layout
 * (e.g. a card that's half-width in a two-column layout and full-width in a
 * single-column one).
 */
const useElementWidth = (): [(node: HTMLElement | null) => void, number] => {
  const [width, setWidth] = useState(0);
  const observerRef = useRef<ResizeObserver | null>(null);

  const measuredRef = useCallback((node: HTMLElement | null) => {
    observerRef.current?.disconnect();

    if (!node) {
      observerRef.current = null;
      return;
    }

    // Reset so consumers don't briefly read a stale width from a previous
    // node (e.g. a conditionally-rendered container remounting) while we
    // wait for the first ResizeObserver callback on this one.
    setWidth(0);

    const observer = new ResizeObserver((entries) => {
      const entry = entries[0];

      if (entry) setWidth(entry.contentRect.width);
    });

    observer.observe(node);
    observerRef.current = observer;
  }, []);

  return [measuredRef, width];
};

export default useElementWidth;
